import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { cloneDeep, isEqual } from 'lodash';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';

import { AccountService } from '../../../../../services/account.service';
import { FunnelService } from '../../../../../services/funnel.service';
import {
  IFunnelStepVariation,
  IProfile,
} from '../../../../../interfaces/graph-types';
import { WatchAccountService } from '../../../../../services/watch-account.service';
import { WatchFunnelService } from '../../../../../services/watch-funnel.service';
import {
  TextAttributesGroup,
  TextAttribute,
  TEXT_ATTRIBUTES,
} from '../../../../../interfaces/text-attribute/TextAttribute';
import { OnAttributeSelectArgs } from '../../../../shared/add-attribute-popup/add-attribute-popup.component';
import { DomSanitizer } from '@angular/platform-browser';
import { SmartButtonComponent } from '../../../../shared/smartbutton/smartbutton.component';

@Component({
  selector: '[account-funnel-step-task]',
  template: require('./account-funnel-step-task.component.pug'),
  styles: [require('./account-funnel-step-task.component.css').toString()],
})
export class AccountFunnelStepTaskComponent implements OnInit {
  @Input()
  formDirty: boolean;
  @Input()
  datafeed: BehaviorSubject<IFunnelStepVariation>;
  @Input()
  baseVariation: BehaviorSubject<IFunnelStepVariation>;
  @Output()
  cancel: EventEmitter<any> = new EventEmitter();
  @Output()
  onUpdate: EventEmitter<void> = new EventEmitter();
  @Output()
  onDelete: EventEmitter<void> = new EventEmitter();
  @Output()
  onFormModified = new EventEmitter<boolean>();
  @ViewChild(SmartButtonComponent, { static: true })
  saveButton: SmartButtonComponent;

  info: IFunnelStepVariation;
  initialInfo: IFunnelStepVariation;
  members$: Observable<IProfile[]>;
  private customAttributes$: Subscription;

  initialContentChange = false;
  isSubjectAttributePopupVisible = false;
  isBodyAttributePopupVisible = false;
  areCustomTextAttributesBeingFetched = true;
  private nameSelectionPosition = {
    start: 0,
    end: 0,
  };

  private funnel_id: string = null;
  private bodyHtml: string = null;
  private bodyText: string = null;

  campsub: any = null;
  sub: any = null;
  editor = null;
  editorContent = '';
  initialEditorContent: string = '';
  inputHtml: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  textAttributes: TextAttributesGroup[] = [];
  bodyFormTextAttributes: TextAttributesGroup[] = [];

  // -------------------------------------------------------------------
  constructor(
    private funnelService: FunnelService,
    private accountService: AccountService,
    private watch: WatchFunnelService,
    private watchAccount: WatchAccountService,
    private sanitizer: DomSanitizer,
  ) {}

  // -------------------------------------------------------------------
  ngOnInit() {
    this.initForm();
    this.injectCustomAttributes();

    this.campsub = this.watch.funnelId().subscribe(funnel_id => {
      this.funnel_id = funnel_id;
    });

    this.members$ = this.accountService.teamMembers(
      this.watchAccount.accountId().getValue(),
    );
  }

  // -------------------------------------------------------------------
  initForm() {
    this.sub = this.datafeed.subscribe(data => {
      this.assignee = null;

      if (!data) {
        data = {};
      }

      if (!data.name) {
        data.name = '';
      }

      if (!data.body) {
        data.body = '';
      }

      this.saveButton.loading = false;
      const safe = this.sanitizer.sanitize(SecurityContext.HTML, data.body);
      this.editorContent = safe;
      this.initialEditorContent = safe;
      this.initialContentChange = false;

      this.bodyHtml = safe;
      this.bodyText = data.bodyExtract;
      if (!data.bodyExtract) {
        this.bodyText = '';
      }

      if (data.waitForCompletion == null) {
        data.waitForCompletion = true;
      }

      this.assignee = data.assignee;
      if (!data.assignee) {
        data.assignee = null;
      }

      this.info = cloneDeep(data);
      this.initialInfo = cloneDeep(this.info);
      this.inputHtml.next(safe);
    });
  }

  // -------------------------------------------------------------------
  handleFormElementChange() {
    const hasFormChanged = !(
      isEqual(this.info, this.initialInfo) &&
      this.bodyHtml === this.initialEditorContent
    );

    this.onFormModified.emit(hasFormChanged);
  }

  // -------------------------------------------------------------------
  injectCustomAttributes() {
    combineLatest(
      this.accountService.candidateAttributes(
        this.watchAccount.accountId().getValue(),
      ),
      this.accountService.candidateTags(
        this.watchAccount.accountId().getValue(),
      ),
    ).subscribe(([customCandidateAttributes, candidateTags]) => {
      if (!customCandidateAttributes || !candidateTags) return;

      const customCandidateTextAttributes: TextAttribute[] = customCandidateAttributes.map(
        ({ name }) => ({
          name,
          value: `candidate.custom.${name}`,
          transformed: false,
        }),
      );

      let candidateAttributesWithCustoms: TextAttributesGroup;
      this.textAttributes = TEXT_ATTRIBUTES.map(group => {
        if (group.name === 'Candidate') {
          candidateAttributesWithCustoms = {
            ...group,
            attributes: [...group.attributes, ...customCandidateTextAttributes],
          };
          return candidateAttributesWithCustoms;
        }
        return group;
      });

      const candidateTagsTextAttributes: TextAttribute[] = candidateTags.map(
        ({ name }) => ({
          name,
          value: `candidate.tag.${name}`,
          transformed: false,
        }),
      );
      this.bodyFormTextAttributes = [
        candidateAttributesWithCustoms,
        { name: 'Candidate tags', attributes: candidateTagsTextAttributes },
      ];

      this.areCustomTextAttributesBeingFetched = false;
    });
  }

  // -------------------------------------------------------------------
  onEditorCreated(quill) {
    this.editor = quill;
  }

  // -------------------------------------------------------------------
  getSubjectSelectionPosition(event: Event) {
    this.handleFormElementChange();
    this.nameSelectionPosition = {
      start: (event.target as HTMLInputElement).selectionStart,
      end: (event.target as HTMLInputElement).selectionEnd,
    };
  }

  // -------------------------------------------------------------------
  toggleSubjectAttributePopup() {
    this.isSubjectAttributePopupVisible = !this.isSubjectAttributePopupVisible;
  }

  // -------------------------------------------------------------------
  closeSubjectAttributePopup() {
    this.isSubjectAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  handleSubjectAttributeSelect({ attribute }: OnAttributeSelectArgs) {
    const {
      start: selectionStartPosition,
      end: selectionEndPosition,
    } = this.nameSelectionPosition;
    const newSubjectArray = this.info.name.split('');
    const charsToInsert = `{{${attribute.value}}}`.split('');

    newSubjectArray.splice(
      selectionStartPosition,
      selectionEndPosition - selectionStartPosition,
      ...charsToInsert,
    );
    this.info.name = newSubjectArray.join('');

    this.isSubjectAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  toggleBodyAttributePopup() {
    this.isBodyAttributePopupVisible = !this.isBodyAttributePopupVisible;
  }

  // -------------------------------------------------------------------
  closeBodyAttributePopup() {
    this.isBodyAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  handleBodyAttributeSelect({
    attribute,
    isFormAttribute,
  }: OnAttributeSelectArgs) {
    const selection = this.editor.getSelection(true);

    let textToInsert = isFormAttribute
      ? `[[${attribute.value}]]`
      : `{{${attribute.value}}}`;

    if (attribute.transformed) {
      textToInsert = attribute.value;
    }

    this.editor.insertText(selection.index, textToInsert);
    this.isBodyAttributePopupVisible = false;
  }

  // -------------------------------------------------------------------
  onTextChanged({ html, text, firstUpdate }) {
    this.bodyHtml = html;
    this.bodyText = text;

    if (!firstUpdate) {
      this.onFormModified.emit(true);
    }
  }

  assignee = null;
  // -------------------------------------------------------------------
  changeAssignee() {
    this.info.assignee = this.assignee;
    this.handleFormElementChange();
  }

  // -------------------------------------------------------------------
  waitForCompletion(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.info.waitForCompletion = !this.info.waitForCompletion;
    this.handleFormElementChange();
    onsuccess();
  }

  // -------------------------------------------------------------------
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
    if (this.campsub) {
      this.campsub.unsubscribe();
    }
    if (this.customAttributes$) {
      this.customAttributes$.unsubscribe();
    }
  }
  // -------------------------------------------------------------------
  getTitle() {
    if (this.info.id) {
      return 'Update step';
    }
    return 'Create step';
  }

  // -------------------------------------------------------------------
  onCancel() {
    this.cancel.emit(null);
  }

  // -------------------------------------------------------------------
  delete(data: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    this.funnelService.deleteStepVariation(
      this.info.id,
      () => {
        this.onDelete.emit();
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  save(data: any, onsuccess: onSmartSuccess, onfailure: onSmartFailure) {
    if (!this.funnel_id) {
      console.log('Funnel id unknown');
      onfailure('Unknown funnel id');
      return;
    }
    // console.log('SaveTask!');
    // console.log(this.funnel_id);
    // console.log(this.info.body);
    // console.log(this.info.waitForCompletion);
    // console.log(this.info.assignee);

    let baseVariationId = null;
    if (this.baseVariation.getValue()) {
      baseVariationId = this.baseVariation.getValue().id;
    }

    this.funnelService.createOrUpdateTaskStep(
      baseVariationId,
      this.funnel_id,
      this.info.id,
      this.info.name,
      this.bodyHtml,
      this.bodyText,
      this.info.waitForCompletion,
      this.info.assignee,
      data => {
        this.onUpdate.emit(data.stepVariationId);
      },
      error => {
        onfailure(error);
      },
    );
  }

  // -------------------------------------------------------------------
  deleteTagCondition(
    data: any,
    onsuccess: onSmartSuccess,
    onfailure: onSmartFailure,
  ) {
    this.funnelService.deleteConditionTags(
      [data.id],
      data => {
        this.onUpdate.emit(data.stepVariationId);
      },
      error => {
        onfailure(error);
      },
    );
  }
}
